
import { Component, Watch } from 'vue-property-decorator';
import MetricPage from '@/components/analytics/MetricPage.vue';
import { namespace } from 'vuex-class';
import VWrapper from '@/components/shared/VWrapper.vue';
import VRangePicker from '@/components/shared/form/VRangePicker.vue';
import AnalyticsLoader from '@/components/analytics/AnalyticsLoader.vue';
import { Venue } from '@/interfaces/models/Venue';
import { Company } from '@/interfaces/models/Company';
import ExportWidget from '@/components/shared/ExportWidget.vue';
import OrderServiceApi from '@/api/http/OrderServiceApi';
import { OrderFlavor } from '@/enums/OrderFlovor';
import Notification from '@/mixins/Notification';
import PaymentApiService from '@/api/http/PaymentApiService';
import ReportingExportService, { ExportFormat, ReportType } from '@/api/http/ReportingExportService';
import type { CreateExportRequest } from '@/api/http/ReportingExportService';
import ExportService from '@/api/http/ExportService';
import { CustomerGroup } from '@/enums/CustomerGroup';
import { mixins } from 'vue-class-component';
import { Download } from '@/interfaces/models/Download';
import { VenueLabel } from '@/interfaces/models/VenueLabel';
import { Permission } from '@/enums/Permission';
import { AxiosResponse } from 'axios';
import { ExportFields } from '@/enums/ExportFields';
import { ExportType } from '@/enums/ExportType';
import { CustomerGroup as CustomerGroupEnum } from '@/enums/CustomerGroup';
import moment from 'moment';
import { User } from '@/interfaces/models/User';
import { toUrl, getVenuesFromCookie } from '@/util/helper';

const app = namespace('app');
const auth = namespace('auth');
const label = namespace('venueLabel');
const download = namespace('download');
const venue = namespace('venue');
const company = namespace('company');

interface ExportFormData {
  venue?: string | string[];
  dndCompanyCode?: string | string[];
  flavor: OrderFlavor;
  from: string;
  to: string;
  lang?: string;
  labels?: string[];
  withoutTags?: boolean;
  expanded?: boolean;
  format?: string;
  venues?: string | string[];
  customerGroups?: CustomerGroup[];
  customerGroup?: CustomerGroup;
}

@Component({
  computed: {
    ExportType() {
      return ExportType;
    },
    ExportFields() {
      return ExportFields;
    },
  },
  components: { ExportWidget, AnalyticsLoader, VRangePicker, MetricPage, VWrapper },
})
export default class Billing extends mixins(Notification) {
  @app.State('venues') public venues!: Venue[];
  @auth.State('user') public authUser!: User;
  @label.State('items') public labels!: VenueLabel[];
  @venue.State('active') public venue!: Venue;
  @company.State('items') public companies!: Company[];
  @label.Action('fetch') public getLabels!: any;
  @download.Action('addDownload') public addDownload!: (d: Download) => void;
  @company.Action('getCompaniesByVenue') public getCompaniesByVenue!: any;

  get defaultCustomerGroup() {
    return this.authUser.customerGroup ? [this.authUser.customerGroup] : null;
  }

  get foodspotExportAvailable() {
    if (this.$isAdmin()) {
      return true;
    }

    const userVenuesList: string[] = getVenuesFromCookie();
    const userVenues: Venue[] = this.venues.filter((venue: Venue) => (userVenuesList || []).includes(venue._id));

    if (!this.$can(Permission.BILLING_VIEW)) {
      return false;
    }

    if (
      this.authUser?.customerGroup !== CustomerGroupEnum.DeanAndDavid &&
      !userVenues.find((venue: Venue) => venue.customerGroup === CustomerGroupEnum.DeanAndDavid)
    ) {
      return false;
    }

    return true;
  }

  public async mounted() {
    if (this.$can(Permission.LABEL_VIEW)) {
      await this.getLabels();
    }

    await this.onVenueForCompanyChange();
  }

  public exportOrderCsv(data: ExportFormData) {
    this.$startLoading('orderCsv');
    const api: OrderServiceApi = new OrderServiceApi();
    api
      .orderCsv({ ...data, lang: this.$i18n.locale })
      .then(() => this.notifySuccess('notification.successfulDownload'))
      .finally(() => {
        this.$stopLoading('orderCsv');
      });
  }

  /**
   * Foodspot orders, generated by the reporting service (`foodspot`) instead of
   * export-service's per-venue endpoint. It is an asynchronous job that
   * downloads here, where the old one was e-mailed to the caller.
   *
   * One venue per run — the handler reads the first entry. A company narrows
   * the export to that company, which as xlsx also means one flat sheet rather
   * than a sheet per company.
   */
  public async exportFoodspot(data: ExportFormData) {
    const venue: string | undefined = this.firstVenue(data);
    if (!venue) {
      this.notifyError('billing.foodspotVenueRequired');
      return;
    }

    this.$startLoading('foodspotExport');
    try {
      await this.queueExport(ReportType.FOODSPOT, this.exportFormat(data), {
        period: this.exportPeriod(data),
        venues: [venue],
        lang: this.exportLang(),
        options: data.dndCompanyCode ? { dndCompanyCode: data.dndCompanyCode } : {},
      });
    } catch (error) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('foodspotExport');
    }
  }

  /**
   * Orders by payment method, generated by the reporting service
   * (`orders_by_payment`) instead of order-service's export endpoint.
   *
   * Either a set of venues or a whole customer group. The handler takes one or
   * the other and lets the group win, so the picked venues are sent on their
   * own rather than being silently dropped — a caller whose group is fixed for
   * them still gets it when they pick no venue.
   */
  public async exportOrderPaymentMethodCsv(data: ExportFormData) {
    const venues: string[] = this.venueList(data);
    const customerGroup: CustomerGroup | undefined = venues.length > 0 ? undefined : data.customerGroup;

    if (venues.length === 0 && !customerGroup) {
      this.notifyError('billing.paymentMethodVenueRequired');
      return;
    }

    this.$startLoading('orderPaymentMethodCsv');
    try {
      await this.queueExport(ReportType.ORDERS_BY_PAYMENT, this.exportFormat(data), {
        period: this.exportPeriod(data),
        ...(customerGroup ? { customerGroup } : { venues }),
        lang: this.exportLang(),
        // The wider variant: per-article and tax columns plus a report header.
        options: { expanded: !!data.expanded },
      });
    } catch (error) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('orderPaymentMethodCsv');
    }
  }

  public exportSalesCsv(data: ExportFormData) {
    const api: OrderServiceApi = new OrderServiceApi();
    const payload = { ...data };
    if (payload.to && payload.to.split(' ').length === 1) {
      payload.to = `${payload.to} 23:59`;
    }
    payload.from = moment(data.from).format('YYYY-MM-DD HH:mm');

    if (payload && payload.format && payload.format === ExportType.PDF) {
      api.salesPdf(payload).then((res: AxiosResponse<{ file: string }>) => {
        this.addDownload({ file: res.data.file, timeout: 60, handler: 'sales', inProgress: false, done: false });
        this.notifyInfo('notification.downloadInProgress');
      });
    } else {
      this.$startLoading('salesCsv');
      api
        .salesCsv({ ...payload, lang: this.$i18n.locale })
        .then(() => this.notifySuccess('notification.successfulDownload'))
        .finally(() => {
          this.$stopLoading('salesCsv');
        });
    }
  }

  public getCustomerGroup(data: ExportFormData): CustomerGroup {
    if (data.venue) {
      const venue: Venue = this.venues.find((v: Venue) => v._id === data.venue)!;
      if (venue) {
        return venue.customerGroup;
      }
    }

    return CustomerGroup.Default;
  }

  /**
   * The invoice summary, now generated by the reporting service
   * (`venue_invoice_summary`) instead of payment-service's per-customer-group
   * endpoints. It is an asynchronous job: queue it, and DownloadHandler polls
   * the job and opens the signed PDF once it is ready.
   *
   * One report for every customer group — the migrated handler renders a single
   * template, where the old flow had a separate Subway document.
   */
  public async exportBillingSummary(data: ExportFormData) {
    const venue: string | undefined = this.firstVenue(data);
    if (!venue) {
      this.notifyError('billing.summaryVenueRequired');
      return;
    }

    this.$startLoading('summary');
    try {
      const api: ReportingExportService = new ReportingExportService();
      const { data: job } = await api.createExport({
        reportType: ReportType.VENUE_INVOICE_SUMMARY,
        // The handler always renders a PDF; the field is required all the same.
        format: ExportFormat.PDF,
        filters: {
          period: `${data.from}/${data.to}`,
          venues: [venue],
          lang: this.exportLang(),
        },
      });

      this.addDownload({ file: job.jobId, timeout: 300, handler: 'export', inProgress: false, done: false });
      this.notifyInfo('notification.downloadInProgress');
    } catch (error) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('summary');
    }
  }

  /** The invoice summary is a single-venue report; the widget may hand over a list. */
  public firstVenue(data: ExportFormData): string | undefined {
    const value: string | string[] | undefined = data.venue ?? data.venues;
    const venue: string | undefined = Array.isArray(value) ? value[0] : value;
    return venue || undefined;
  }

  /** The venues the widget handed over, as the list every report expects. */
  public venueList(data: ExportFormData): string[] {
    const value: string | string[] | undefined = data.venue ?? data.venues;
    if (Array.isArray(value)) {
      return value.filter((venue: string) => !!venue);
    }
    return value ? [value] : [];
  }

  /**
   * The reporting API takes an inclusive `YYYY-MM-DD/YYYY-MM-DD` range and
   * rejects anything else, so a widget that hands over a time of day as well
   * has it trimmed off here. The reports bucket by whole Europe/Berlin days.
   */
  public exportPeriod(data: ExportFormData): string {
    return `${moment(data.from).format('YYYY-MM-DD')}/${moment(data.to).format('YYYY-MM-DD')}`;
  }

  /**
   * The reporting API localizes reports in German, English and French only,
   * and rejects the request outright for anything else. The backoffice also
   * speaks Italian and Dutch, which fall back the way the rest of the app
   * does (`fallbackLocale`) rather than failing the export.
   */
  public exportLang(): string {
    const supported: string[] = ['de', 'en', 'fr'];
    return supported.includes(this.$i18n.locale) ? this.$i18n.locale : 'en';
  }

  /** The file type the widget's select asked for; csv unless it says otherwise. */
  public exportFormat(data: ExportFormData): ExportFormat {
    return data.format === ExportType.XLSX ? ExportFormat.XLSX : ExportFormat.CSV;
  }

  /**
   * Queues a reporting-service export and hands the job to DownloadHandler,
   * which polls it and opens the signed URL once it is ready.
   */
  public async queueExport(
    reportType: ReportType,
    format: ExportFormat,
    filters: CreateExportRequest['filters'],
  ): Promise<void> {
    const api: ReportingExportService = new ReportingExportService();
    const { data: job } = await api.createExport({ reportType, format, filters });

    this.addDownload({ file: job.jobId, timeout: 300, handler: 'export', inProgress: false, done: false });
    this.notifyInfo('notification.downloadInProgress');
  }

  public legacyExportBillingPlainSummary(data: ExportFormData) {
    const api: PaymentApiService = new PaymentApiService();
    api.billingSummaryPlainPdf(data).then((res: AxiosResponse<{ file: string }>) => {
      this.addDownload({ file: res.data.file, timeout: 60, handler: 'payment', inProgress: false, done: false });
      this.notifyInfo('notification.downloadInProgress');
    });
  }

  public exportBillingPlainSummary(data: ExportFormData) {
    const api: PaymentApiService = new PaymentApiService();
    api
      .billingSummaryPlainPdf({ ...data, locale: this.$i18n.locale, v2: true })
      .then((res: AxiosResponse<{ file: string }>) => {
        this.addDownload({ file: res.data.file, timeout: 60, handler: 'payment', inProgress: false, done: false });
        this.notifyInfo('notification.downloadInProgress');
      });
  }

  public exportBilling(data: ExportFormData) {
    const api: PaymentApiService = new PaymentApiService();
    if (
      this.getCustomerGroup(data) === CustomerGroup.Subway ||
      this.getCustomerGroup(data) === CustomerGroup.SubwayFr ||
      this.getCustomerGroup(data) === CustomerGroup.SubwayLu
    ) {
      api.billingPdfSubway({ ...data, locale: this.$i18n.locale, v2: true }).then(() => {
        this.notifyInfo('notification.successfulDownload');
      });
      return;
    }
    if (data.format === ExportType.CSV) {
      api.billingCsv({ ...data, locale: this.$i18n.locale, v2: true }).then((res: AxiosResponse<string>) => {
        toUrl(
          new Blob([res.data], { type: 'text/csv;charset=utf-8;' }),
          `billing-${moment().format('DD-MM-YYYY')}.csv`,
        );
        this.notifyInfo('notification.downloadInProgress');
      });
      return;
    }
    api.billingPdf({ ...data, locale: this.$i18n.locale, v2: true }).then(() => {
      this.notifyInfo('notification.successfulDownload');
    });
  }

  public exportSalesGroup(data: ExportFormData) {
    this.$startLoading('exportSalesGroup');
    const api: OrderServiceApi = new OrderServiceApi();

    let format: string = ExportType.CSV;
    if (data && data.format && data.format === ExportType.XLSX) {
      format = ExportType.XLSX;
    }

    data.venues = data?.venue ? data.venue : [];
    delete data.venue;
    api
      .salesGroup({ ...data })
      .then(() => this.notifySuccess('notification.successfulDownload'))
      .finally(() => {
        this.$stopLoading('exportSalesGroup');
      });
  }

  public exportDatev(data: Partial<ExportFormData>) {
    const api: ExportService = new ExportService();
    this.$startLoading('datev');
    data.venues = data?.venue ? data.venue : [];
    delete data.venue;

    api
      .datevCsv(data as { from: string; to: string; customerGroups: CustomerGroup[]; venues: string[] })
      .then((res: AxiosResponse<string>) => {
        toUrl(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }), `datev-${moment().format('DD-MM-YYYY')}.csv`);
        this.notifyInfo('notification.downloadInProgress');
      })
      .finally(() => {
        this.$stopLoading('datev');
      });
  }

  public exportWallet(data: Partial<ExportFormData>) {
    const api: ExportService = new ExportService();

    const dataFormatted = {
      customerGroup: data.customerGroup,
      from: data.from,
      ...(data.to ? { to: data.to } : {}),
    };
    this.$startLoading('wallet');

    api
      .walletExport(dataFormatted as { customerGroup: CustomerGroup; from: string; to?: string })
      .then((res: AxiosResponse<string>) => {
        toUrl(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }), `wallet-${moment().format('DD-MM-YYYY')}.csv`);
        this.notifyInfo('notification.downloadInProgress');
      })
      .finally(() => {
        this.$stopLoading('wallet');
      });
  }

  public exportArticlesOverview(data: ExportFormData) {
    const api: PaymentApiService = new PaymentApiService();
    this.$startLoading('exportArticlesOverview');

    let fileFormat: ExportType.PDF | ExportType.CSV | undefined;

    if (data.format === ExportType.CSV) {
      fileFormat = data.format;
    } else {
      fileFormat = undefined;
    }

    const { format, ...rest } = data;
    const payload = { ...rest, lang: this.$i18n.locale, v2: true };

    api
      .articlesOverview(payload, fileFormat)
      .then((res: AxiosResponse<{ file: string }>) => {
        const fileData = res?.data?.file || res?.data;

        if (!fileData) {
          return this.notifyError('notification.downloadFailed');
        }

        if (!fileFormat) {
          this.addDownload({ file: fileData as any, timeout: 60, handler: 'payment', inProgress: false, done: false });
          this.notifyInfo('notification.downloadInProgress');
        } else {
          toUrl(
            new Blob([fileData as any], { type: 'text/csv;charset=utf-8;' }),
            `articles-overview-${moment().format('DD-MM-YYYY')}.csv`,
          );
          this.notifyInfo('notification.downloadInProgress');
        }
      })
      .finally(() => {
        this.$stopLoading('exportArticlesOverview');
      });
  }

  public handleErrorValidation(data: string[]) {
    this.notifyError('billing.form.requiredField');
  }

  @Watch('venue')
  public async onVenueForCompanyChange(venueData?: string | object) {
    const venueId: string | undefined = typeof venueData === 'string' ? venueData : (venueData as Venue)?._id;
    const venue: string = venueId || this.venue?._id;

    if (this.$can(Permission.DND_COMPANY_VIEW) && venue) {
      await this.getCompaniesByVenue({ id: venue });
    }
  }
}
